import React, { useState, useEffect, useRef } from 'react';
import Sessions from './Sessions';
import Messages from './Messages';
import Websocket from 'react-websocket';
import axios from 'axios';
import { useIpcContactName } from '../../../hooks/useIpcContactName';
import Spinner from '../../../components/Widgets/Spinner';
import MfaAuthModal from '../../../components/Widgets/MfaAuthModal';

const Index = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [cloudId, setCloudId] = useState('');
  const [websocketUrl, setWebsocketUrl] = useState('');
  const [newWebSocketMessage, setNewWebSocketMessage] = useState(null);
  const [messageWebSocketData, setMessageWebSocketData] = useState(null);
  const [contacts, setContacts] = useState({});
  const [contactsLowerCase, setContactsLowerCase] = useState({});
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fromIndex, setFromIndex] = useState(false);
  const [dids, setDids] = useState([]);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [initialRecipient, setInitialRecipient] = useState(null);
  const [initialSessionId, setInitialSessionId] = useState(null);
  const mfaAuthRef = useRef(null);
  const { getDisplayName, ipcInitialized, ipcManager } = useIpcContactName(cloudId, contactsLowerCase);

  // Clear host app badge when messaging page opens (IPC ready)
  // https://ipc-sdk.acrobits.net/classes/IPCManager.html#badgeupdate
  useEffect(() => {
    if (!ipcInitialized || !ipcManager) return;
    try {
      ipcManager.badgeUpdate(0, false);
    } catch (err) {
      console.error('[Badge] Failed to clear badge on open:', err);
    }
  }, [ipcInitialized, ipcManager]);

  // useEffect(() => {
  //   const href = window.location.href;
  //   if (navigator.clipboard?.writeText) {
  //     navigator.clipboard.writeText(href).catch(() => {});
  //   }
  //   alert(href);
  // }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    var url = new URL(window.location.href);
    let myCloudId = url.searchParams.get("cloud_id")
    myCloudId = myCloudId.replaceAll("*","");
    let myUsername = url.searchParams.get('username');
    let myPassword = url.searchParams.get('password');
    const checkPassword = atob(myPassword);
    if(checkPassword.startsWith("V2=")){
      myPassword = checkPassword.replace("V2=","");
    }
    let fromIndex = url.searchParams.get('from_index');
    let newMessage = url.searchParams.get('new_message');
    let toUser = url.searchParams.get('to_user');
    let sessionId = url.searchParams.get('session_id');
    
    setCloudId(myCloudId);
    setPassword(myPassword);
    setFromIndex(fromIndex);
    
    // If new_message=true and to_user is present, pass it to Sessions
    if (newMessage === 'true' && toUser) {
      setInitialRecipient(toUser);
      setShowNewMessageModal(true);
    }

    // If session_id is present, open that session after sessions load, then remove from URL
    if (sessionId) {
      setInitialSessionId(sessionId);
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
    getMessagingStyles(myCloudId);

    const payLoad = {
      user: myUsername,
      password: myPassword,
      cloud_id: myCloudId
    }
    
    axios.post(`${process.env.REACT_APP_API_URL}/signin`, payLoad)
    .then(async (response) => {
      if (response.data.sso === "sso_required") {
        const completed = await mfaAuthRef.current?.openAndAuthenticate({
          authType: 'sso',
          scope: response.data.scope,
          cloudId: myCloudId
        });
        if (!completed) return;
        response = await axios.post(`${process.env.REACT_APP_API_URL}/signin`, payLoad);
      }

      if (response.data.mfa === "mfa_required") {
        const authenticated = await mfaAuthRef.current?.openAndAuthenticate({
          username: response.data.username || `${response.data.user}@${response.data.domain}`,
          cloudId: myCloudId,
          password: myPassword,
          mfaType: response.data.mfa_type,
          mfaVendor: response.data.mfa_vendor,
          accessToken: response.data.access_token
        });
        if (!authenticated) return;
      }

      myUsername = response.data.uid;
      setUsername(myUsername);
      fetchContacts(myUsername, myPassword, myCloudId);
      const ws = `${process.env.REACT_APP_WSS_URL}` + myUsername +":"+myCloudId;
      setWebsocketUrl(ws);
       
    }).catch(err => {
        console.log(err.response.data);
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getMessagingStyles = async (cloudId) => {
    const myCloudId = cloudId.replaceAll("*","");
    const payLoad = {
      cloud_id: myCloudId
    }
    axios.post(`${process.env.REACT_APP_ADMIN_API_URL}getMessagingStyles`, payLoad)
    .then(response => {
      localStorage.setItem("messaging_styles", JSON.stringify(response.data));
    })
    .catch(error => {
      console.error('Error fetching messaging styles:', error);
    });
  };

  const fetchContacts = async (username, password, cloudId) => {
    try {
      const payLoad = {
        username: username,
        password: password,
        cloud_id: cloudId
      };
      
      const response = await axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/fetchContacts`, payLoad);
      setContacts(response.data);
      
      // Create a new object with lowercase keys
      const contactsLower = {};
      Object.entries(response.data).forEach(([key, value]) => {
        contactsLower[key.toLowerCase()] = value;
      });
      setContactsLowerCase(contactsLower);
      // Fetch DIDs after contacts
      fetchDids(username, password, cloudId);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  // Fetch DIDs
  const fetchDids = async (username, password, cloudId) => {
    try {
      const payload = {
        cloud_username: username,
        cloud_password: password,
        cloud_id: cloudId
      };
      const response = await axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/getDidSharingAndOwnDids`, payload);
      setDids(response.data);
    } catch (error) {
      console.error('Error fetching DIDs:', error);
    }
  };

  const hasContact = (chatName) => {
    const myChatName = chatName.toLowerCase();
    return contactsLowerCase[myChatName];
  }

  const handleBack = () => {
    setSelectedChat(null);
  };

  const handleData = (dataString) => {
    if(dataString === 'Ping'){
        return;
    }
    const data = JSON.parse(dataString);
    
    // Pass the WebSocket message to the Sessions component
    setNewWebSocketMessage(data);
    
    // If this message belongs to the currently selected chat, pass it to the Messages component
    if (selectedChat && data.SessionID === selectedChat.id) {
      setMessageWebSocketData(data);
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 relative" style={{ height: '100dvh' }}>
      {/* Banner - Only shown in dev environment */}
      {process.env.REACT_APP_ENVIRONMENT === "dev" && (
        <div 
          style={(() => {
            const styles = localStorage.getItem("messaging_styles");
            let bannerStyles = {};
            
            if (styles) {
              try {
                const parsedStyles = JSON.parse(styles);
                bannerStyles = {
                  backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                  color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                };
              } catch (e) {
                console.error('Error parsing messaging styles:', e);
              }
            }
            return bannerStyles;
          })()}
          className={`absolute top-0 left-1/2 transform -translate-x-1/2 px-6 py-2 rounded-b-lg shadow-md z-50
            ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white' : ''}`}
        >
          Test
        </div>
      )}
      {/* Show loading until IPC is initialized */}
      {!ipcInitialized ? (
        <div className="flex w-full h-full items-center justify-center">
          <Spinner data="" />
        </div>
      ) : (
        <>
          {/* Sessions/Conversations Panel */}
          <div className={
            `${isMobile && selectedChat ? 'hidden' : 'flex'}
            ${!isMobile ? 'min-w-[500px]' : 'w-full'}
            flex-col h-full min-h-0 overflow-hidden bg-white border-r border-gray-200`
          }>
            {
              websocketUrl?
                <Websocket url={websocketUrl}
                    onMessage={handleData}/>
              :null
            }
            {
              username && password && cloudId && (
                <Sessions 
                  onSelectChat={setSelectedChat} 
                  fromIndex={fromIndex}
                  selectedChat={selectedChat}
                  newWebSocketMessage={newWebSocketMessage}
                  contacts={contacts}
                  getDisplayName={getDisplayName}
                  username={username}
                  password={password}
                  cloudId={cloudId}
                  hasContact={hasContact}
                  dids={dids}
                  showNewMessageModal={showNewMessageModal}
                  setShowNewMessageModal={setShowNewMessageModal}
                  initialRecipient={initialRecipient}
                  setInitialRecipient={setInitialRecipient}
                  initialSessionId={initialSessionId}
                  setInitialSessionId={setInitialSessionId}
                  ipcManager={ipcManager}
                  ipcInitialized={ipcInitialized}
                />
              )
            }
            {/* NewMessageModal usage example, pass dids as prop */}
            {/* <NewMessageModal dids={dids} ...otherProps /> */}
          </div>

          {/* Messages Panel */}
          <div className={
            `${isMobile && !selectedChat ? 'hidden' : 'flex'}
            ${!isMobile ? 'w-[100%]' : 'w-full'}
            flex-col h-full min-h-0 overflow-hidden bg-white`
          }>
            {selectedChat ? (
              <Messages 
                chat={selectedChat} 
                onBack={handleBack}
                isMobile={isMobile}
                webSocketData={messageWebSocketData}
                getDisplayName={getDisplayName}
                username={username}
                password={password}
                cloudId={cloudId}
              />
            ) : (
              <div className="hidden md:flex items-center justify-center h-full">
                <p className="text-gray-500">Select a conversation to start messaging</p>
              </div>
            )}
          </div>
        </>
      )}
      <MfaAuthModal ref={mfaAuthRef} />
    </div>
  );
}

export default Index;
